import { api } from "../api/http.js";
import type { Balances } from "../state/balances.js";
import type { EnclavePower } from "@zend/rules";
import type { AutoCollectorView, BaseLoopState, CollectionResult, DrillView, DrillResult } from "./loop-types.js";

export type ResourceKey = "RAT" | "POL" | "ALY" | "ENC" | "SCR";
export type Cost = { RAT: number; POL: number; ALY: number; ENC: number; SCR?: number };
/** Which depot crates would cover a cost the wallet cannot pay (D75). */
export interface CoverPlan {
  shortfall: Record<string, number>;
  opens: { itemId: string; resource: string; amount: number; qty: number }[];
  missing: Record<string, number>;
  blocked: string[];
  leftover: Record<string, number>;
  sufficient: boolean;
}

export interface BuildingView {
  id: string;
  buildingId: string;
  level: number;
  x: number;
  y: number;
  rot: number;
  w: number;
  h: number;
  state: "idle" | "constructing" | "upgrading";
  targetLevel: number;
  startedAt: number | null;
  finishesAt: number | null;
  remainingMs: number;
  skin: string | null;
  upgradeable: boolean;
  canUpgrade: { ok: boolean; reasons: string[] };
  next: { level: number; cost: Cost; timeMs: number } | null;
  attributes: Record<string, number>;
  nextAttributes: Record<string, number> | null;
}

export interface QueueInfo {
  slots: number;
  used: number;
  rentals: { slot: number; until: number }[];
  permanent: number;
  builders?: { slot: number; active: boolean; source: "free" | "vip" | "purchased" | "legacy-vip" | "rental" | "locked"; credits: number; until: number | null }[];
  /** The bench this queue can still be bought outright, or null when it is already owned (D95). */
  purchase?: { slot: number; credits: number } | null;
}

export interface ZoneView {
  id: string;
  state: "locked" | "reclaiming" | "open";
  unlockHq: number;
  rect: { x: number; y: number; w: number; h: number };
  finishesAt: number | null;
  cost: Cost | null;
  timeMs: number;
}

export interface UnlockView {
  buildingId: string;
  unlocked: boolean;
  unlockHq: number;
  allowedCopies: number;
  built: number;
  nextCopyHq: number | null;
  placement: { cost: Cost; timeMs: number };
}

/** One line behind the power balance (D126). */
export interface PowerLineView { rowId?: string; id: string; level: number; amount: number }

export interface BaseState {
  /** Absent only in historical saved-state fixtures. Current API responses always include the colony loop. */
  loop?: BaseLoopState;
  enclave: { accountId: string; name: string; hqLevel: number; burningUntil: number | null; foundedAt: number; power: number; powerBreakdown?: EnclavePower };
  buildings: BuildingView[];
  queues: { build: QueueInfo; research: QueueInfo };
  zones: ZoneView[];
  power: { produced: number; consumed: number; deficit: boolean; efficiencyPct: number; raw?: number; sources?: PowerLineView[]; consumers?: PowerLineView[] };
  production: Record<string, number>;
  capacity: number;
  /** Capacity per resource and the vault's protected share (D107); absent only in historical fixtures. */
  storage?: { capacity: number; protectedPct: number; protectedAmount: number };
  gate: { average: number; required: number; ok: boolean; maxed: boolean; drags: { id: string; level: number; gap: number }[] };
  protocols: { protocolId: string; until: number }[];
  unlocked: UnlockView[];
  grid: { size: number; core: { x: number; y: number; w: number; h: number } };
  bonuses: { buildSpeedPct: number; productionPct: number; researchSpeedPct: number };
  layouts: { slot: number; savedAt: number }[];
  layoutSlots: number;
}

export interface ResearchNodeView {
  nodeId: string;
  tree: string;
  level: number;
  maxLevel: number;
  tier: number;
  researching: boolean;
  targetLevel: number;
  startedAt: number | null;
  finishesAt: number | null;
  remainingMs: number;
  canStart: { ok: boolean; reasons: string[] };
  next: { level: number; cost: Cost & { SCR: number }; badges?: number; timeMs: number } | null;
  effect: { key: string; perLevel: number; current: number };
}

export interface ResearchState {
  nodes: ResearchNodeView[];
  labLevel: number;
  queue: { slots: number; used: number };
  bonuses: Record<string, number>;
}

const building = (p: Promise<{ building: BuildingView }>) => p.then((d) => d.building);

/** Thin typed wrappers over the base and research routes; every call returns fresh server views. */
export const baseApi = {
  autoCollector: () => api<AutoCollectorView>("GET", "/api/base/autocollector"),
  setAutoCollector: (enabled: boolean, revision: number) => api<AutoCollectorView>("PUT", "/api/base/autocollector", { enabled, revision }),
  collect: (rowId: string, requestId: string) => api<CollectionResult>("POST", `/api/base/buildings/${rowId}/collect`, { requestId }),
  collectAll: (requestId: string) => api<CollectionResult>("POST", "/api/base/collect-all", { requestId }),
  acknowledgeRepairs: () => api<{ ok: true }>("POST", "/api/base/beacons/repair/read"),
  startDrill: () => api<DrillView>("POST", "/api/base/drill/start"),
  tapDrill: (id: string, index: number) => api<DrillView>("POST", `/api/base/drill/${id}/tap`, { index }),
  finishDrill: (id: string) => api<DrillResult>("POST", `/api/base/drill/${id}/finish`),
  state: () => api<BaseState>("GET", "/api/base"),
  rename: (name: string) => api<{ name: string }>("PATCH", "/api/base/name", { name }),
  place: (buildingId: string, x: number, y: number, rot: number, cover = false) => building(api("POST", "/api/base/place", { buildingId, x, y, rot, ...(cover ? { cover } : {}) })),
  upgrade: (rowId: string, cover = false) => building(api("POST", `/api/base/buildings/${rowId}/upgrade`, cover ? { cover } : undefined)),
  coverPlan: (cost: Partial<Cost>) => api<{ plan: CoverPlan }>("POST", "/api/inventory/cover-plan", { cost }, { readOnly: true }).then((d) => d.plan),
  speedup: (rowId: string, itemId: string, qty: number) => building(api("POST", `/api/base/buildings/${rowId}/speedup`, { itemId, qty })),
  instant: (rowId: string) => building(api("POST", `/api/base/buildings/${rowId}/instant`)),
  move: (rowId: string, x: number, y: number, rot: number) => building(api("POST", `/api/base/buildings/${rowId}/move`, { x, y, rot })),
  placeDecoration: (decoId: string, x: number, y: number, rot: number) => api<{ base: BaseState; balances: Balances; collectionRef?: string }>("POST", "/api/base/decorations", { decoId, target: { x, y }, rot }),
  moveDecoration: (id: string, x: number, y: number, rot: number) => api<{ base: BaseState; balances: Balances; collectionRef?: string }>("POST", `/api/base/decorations/${id}/move`, { x, y, rot }),
  removeDecoration: (id: string) => api<{ base: BaseState; balances: Balances; collectionRef?: string }>("DELETE", `/api/base/decorations/${id}`),
  /** Raises a standing decoration a level with further copies of its item (D93). */
  upgradeDecoration: (id: string) => api<{ base: BaseState; balances: Balances }>("POST", `/api/base/decorations/${id}/upgrade`),
  cancel: (rowId: string) => api<{ refunded: Record<string, number>; removed: boolean }>("POST", `/api/base/buildings/${rowId}/cancel`),
  rentQueue: (kind: "build" | "research", period: "day" | "week" | "month", slot?: number) => api<{ queue: QueueInfo }>("POST", `/api/base/queues/${kind}/rent`, { period, ...(slot === undefined ? {} : { slot }) }),
  reclaimZone: (zoneId: string, cover = false) => api<{ zone: ZoneView }>("POST", `/api/base/zones/${zoneId}/reclaim`, cover ? { cover } : undefined),
  activateProtocol: (protocolId: string, hours: number, currency: "credits" | "scrip") => api<{ protocolId: string; until: number }>("POST", `/api/base/protocols/${protocolId}`, { hours, currency }),
  saveLayout: (slot: number) => api<{ ok: true }>("PUT", `/api/base/layouts/${slot}`),
  applyLayout: (slot: number) => api<{ moved: number }>("POST", `/api/base/layouts/${slot}/apply`),
};

export const researchApi = {
  state: () => api<ResearchState>("GET", "/api/research"),
  start: (nodeId: string, cover = false) => api<{ node: ResearchNodeView }>("POST", `/api/research/${nodeId}/start`, cover ? { cover } : undefined),
  speedup: (nodeId: string, itemId: string, qty: number) => api<{ node: ResearchNodeView }>("POST", `/api/research/${nodeId}/speedup`, { itemId, qty }),
  instant: (nodeId: string) => api<{ node: ResearchNodeView }>("POST", `/api/research/${nodeId}/instant`),
  cancel: (nodeId: string) => api<{ refunded: Record<string, number> }>("POST", `/api/research/${nodeId}/cancel`),
  /** D118: five drone cores → one research badge. */
  convertBadges: (count: number) => api<{ badges: number; droneCores: number }>("POST", "/api/research/badges", { count }),
};
